/*
  Bundle upsell: adds this product plus any ticked companion products to the
  cart in one request.

  Uses Shopify's /cart/add.js with an items array, then hands the response to
  Dawn's cart drawer so the UI updates exactly as it does for a normal add.

  The main product's variant is read from the live product form at click time,
  so whatever size the shopper picked is what gets added.
*/
class ProductBundle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-bundle-add]');
    this.errorEl = this.querySelector('[data-bundle-error]');
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.rows = Array.from(this.querySelectorAll('[data-bundle-row]'));
    if (!this.button) return;

    this.onClick = this.onClick.bind(this);
    this.refresh = this.refresh.bind(this);

    this.button.addEventListener('click', this.onClick);
    this.addEventListener('change', this.refresh);

    // The main product's price changes with its variant, and Dawn re-renders
    // that node on every variant change.
    this.priceNode = document.getElementById(this.dataset.priceId);
    if (this.priceNode && 'MutationObserver' in window) {
      this.priceObserver = new MutationObserver(this.refresh);
      this.priceObserver.observe(this.priceNode, { childList: true, subtree: true });
    }

    this.refresh();
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.refresh);
    if (this.priceObserver) this.priceObserver.disconnect();
  }

  get cart() {
    return (
      document.querySelector('cart-notification') || document.querySelector('cart-drawer') || null
    );
  }

  mainVariantId() {
    const form = document.getElementById(this.dataset.productFormId);
    const input = form ? form.querySelector('input[name="id"]') : null;
    return input && input.value ? input.value : this.dataset.mainVariant;
  }

  mainPrice() {
    // Read the live price off the page so a variant change is reflected here
    // without needing a second source of truth.
    const raw = this.dataset.mainPrice;
    return raw ? parseInt(raw, 10) : 0;
  }

  selectedRows() {
    return this.rows.filter((row) => {
      const box = row.querySelector('[data-bundle-check]');
      return !box || box.checked;
    });
  }

  rowVariant(row) {
    const select = row.querySelector('[data-bundle-variant]');
    if (select) {
      return {
        id: select.value,
        price: parseInt(select.selectedOptions[0].dataset.price || '0', 10),
      };
    }
    return {
      id: row.dataset.variantId,
      price: parseInt(row.dataset.variantPrice || '0', 10),
    };
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';

    // The row prices are rendered with money_without_trailing_zeros, so a
    // total of $298.00 next to items reading $70 looks like a different
    // currency. Drop the decimals when there are none.
    const whole = cents % 100 === 0;
    const amount = whole ? String(cents / 100) : (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDecimals = Math.round(cents / 100)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals);
  }

  refresh() {
    const selected = this.selectedRows();

    if (this.totalEl) {
      const total = selected.reduce((sum, row) => sum + this.rowVariant(row).price, this.mainPrice());
      this.totalEl.textContent = this.formatMoney(total);
    }

    // Nothing ticked means there is no bundle left to add, only the product
    // itself, which the main button already does.
    this.button.disabled = selected.length === 0;

    const label = this.querySelector('[data-bundle-label]');
    if (label) {
      const one = this.dataset.labelOne || 'Add both to cart';
      const many = this.dataset.labelMany || 'Add all to cart';
      label.textContent = selected.length > 1 ? many : one;
    }
  }

  setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  onClick(event) {
    event.preventDefault();

    const mainId = this.mainVariantId();
    const selected = this.selectedRows();

    if (!mainId || selected.length === 0) {
      this.setError(this.dataset.errorText || 'Please choose an option first.');
      return;
    }

    const items = [{ id: Number(mainId), quantity: 1 }];

    for (const row of selected) {
      const variant = this.rowVariant(row);
      if (!variant.id) {
        this.setError(this.dataset.errorText || 'Please choose an option for every item.');
        return;
      }
      items.push({ id: Number(variant.id), quantity: 1 });
    }

    this.setError('');
    this.button.classList.add('is-loading');
    this.button.setAttribute('aria-busy', 'true');

    const cart = this.cart;
    const body = { items };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.setError(response.description || response.message || 'Could not add to cart.');
          return;
        }

        // The items are in the cart at this point. Dawn's cart notification is
        // built around a single-product response, so rendering a multi-item add
        // can throw. That must never surface as an error on a successful add.
        try {
          if (cart && typeof cart.renderContents === 'function') {
            if (typeof cart.setActiveElement === 'function') {
              cart.setActiveElement(document.activeElement);
            }
            cart.renderContents(response);
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        } catch (renderError) {
          console.warn('[bundle] cart UI could not render the add, opening the cart', renderError);
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch((error) => {
        console.warn('[bundle] add to cart failed', error);
        this.setError('Something went wrong. Please try again.');
      })
      .finally(() => {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-busy');
      });
  }
}

if (!customElements.get('product-bundle')) {
  customElements.define('product-bundle', ProductBundle);
}
